import { useState } from "react";
import { Box, Stack } from "@mui/material";

// Shared with CreatorRemarksTab.jsx (the payment-history "team ratings" tab) —
// single source for the star UI so a rating always looks the same wherever
// it's given, whether that's the full payment-history page or the compact
// review modal opened from a plan's InfluencerTable.
export const RATING_META = [
    null,
    { label: 'Poor',          color: '#EF4444', bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.22)',   icon: '⚑', iconColor: '#DC2626', statusText: 'Flagged'     },
    { label: 'Below Average', color: '#F97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.22)',  icon: '⚑', iconColor: '#EA580C', statusText: 'Flagged'     },
    { label: 'Average',       color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.22)',  icon: '◉', iconColor: '#D97706', statusText: 'Average'     },
    { label: 'Good',          color: '#10B981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.22)',  icon: '✦', iconColor: '#059669', statusText: 'Recommended' },
    { label: 'Excellent',     color: '#6366F1', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.22)',  icon: '★', iconColor: '#4F46E5', statusText: 'Outstanding' },
];

// Proper 5-point star polygon path (24×24 viewBox)
export const STAR_PATH = "M12 1.8L14.78 7.43L21 8.36L16.5 12.74L17.56 18.94L12 16.02L6.44 18.94L7.5 12.74L3 8.36L9.22 7.43L12 1.8Z";

// Renders the gold gradient definition once — all star SVGs reference url(#star-gold)
export function GoldStarDefs() {
    return (
        <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <defs>
                <linearGradient id="star-gold" x1="25%" y1="0%" x2="75%" y2="100%">
                    <stop offset="0%"   stopColor="#FBE96A" />
                    <stop offset="42%"  stopColor="#E5A012" />
                    <stop offset="100%" stopColor="#B8720A" />
                </linearGradient>
                <linearGradient id="star-gold-bright" x1="25%" y1="0%" x2="75%" y2="100%">
                    <stop offset="0%"   stopColor="#FDF0A0" />
                    <stop offset="42%"  stopColor="#F0B824" />
                    <stop offset="100%" stopColor="#C07C10" />
                </linearGradient>
            </defs>
        </svg>
    );
}

export function StarPicker({ value, onChange }) {
    const [hovered, setHovered] = useState(0);
    const effective = hovered || value;

    return (
        <Stack direction="row" spacing={0.6}>
            {[1, 2, 3, 4, 5].map(star => {
                const filled = star <= effective;
                const isHovered = star <= hovered;
                return (
                    <Box
                        key={star}
                        onClick={() => onChange(star)}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            transform: filled ? 'scale(1.1) translateY(-3px)' : 'scale(1)',
                            transition: 'transform 0.14s cubic-bezier(0.34,1.56,0.64,1)',
                            '&:hover': { transform: 'scale(1.22) translateY(-5px)' },
                            filter: filled
                                ? `drop-shadow(0 3px 8px rgba(210,140,10,${isHovered ? '0.65' : '0.4'}))`
                                : 'none',
                        }}
                    >
                        <svg width={38} height={38} viewBox="0 0 24 24" style={{ display: 'block' }}>
                            <path
                                d={STAR_PATH}
                                fill={filled ? (isHovered ? 'url(#star-gold-bright)' : 'url(#star-gold)') : '#E8E2D6'}
                                style={{ transition: 'fill 0.12s ease' }}
                            />
                        </svg>
                    </Box>
                );
            })}
        </Stack>
    );
}

export function ReadonlyStars({ value, size = 18 }) {
    return (
        <Stack direction="row" spacing={0.3}>
            {[1, 2, 3, 4, 5].map(star => (
                <Box
                    key={star}
                    sx={{
                        display: 'flex',
                        filter: star <= value ? 'drop-shadow(0 1px 3px rgba(210,140,10,0.35))' : 'none',
                    }}
                >
                    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block' }}>
                        <path d={STAR_PATH} fill={star <= value ? 'url(#star-gold)' : '#E8E2D6'} />
                    </svg>
                </Box>
            ))}
        </Stack>
    );
}
